import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Star, Clock, Bike, Plus, ChevronLeft } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

export default function RestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const { formatPrice } = useCurrency();
  const [r, setR] = useState(null);
  const { addItem, count } = useCart();

  useEffect(() => {
    api.get(`/restaurants/${id}`).then(res => setR(res.data));
  }, [id]);

  const categories = useMemo(() => {
    if (!r) return [];
    const map = new Map();
    r.menu.forEach((it) => {
      const arr = map.get(it.category) || [];
      arr.push(it);
      map.set(it.category, arr);
    });
    return Array.from(map.entries());
  }, [r]);

  if (!r) return <CustomerLayout><div className="p-8">Loading...</div></CustomerLayout>;

  return (
    <CustomerLayout>
      <div className="relative">
        <div className="h-48 md:h-64 bg-gray-200 dark:bg-gray-800 overflow-hidden">
          <img src={r.banner_url || r.image_url} alt={r.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
        <button onClick={() => navigate(-1)} className="absolute top-4 start-4 w-10 h-10 rounded-full bg-white/90 dark:bg-black/60 backdrop-blur flex items-center justify-center" data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="max-w-4xl mx-auto px-4 -mt-12 relative">
          <div className="so-card p-5">
            <div className="font-bold text-2xl">{r.name}</div>
            <div className="text-sm text-[var(--so-text-soft)]">{r.cuisine} · {r.address}</div>
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> <b>{r.rating}</b> <span className="text-[var(--so-text-soft)]">({r.review_count})</span></span>
              <span className="flex items-center gap-1 text-[var(--so-text-soft)]"><Clock className="w-4 h-4" /> {r.avg_prep_minutes} min</span>
              <span className="flex items-center gap-1 text-[var(--so-text-soft)]"><Bike className="w-4 h-4" /> {formatPrice(r.delivery_fee)}</span>
              <span className="text-xs text-[var(--so-text-soft)]">{t("minOrder")}: {formatPrice(r.min_order)}</span>
            </div>
          </div>

          <div className="mt-6 space-y-8">
            {categories.map(([cat, items]) => (
              <section key={cat} data-testid={`menu-cat-${cat}`}>
                <h3 className="font-bold text-lg mb-3">{cat}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {items.map((it) => (
                    <div key={it.id} className="so-card p-4 flex gap-3" data-testid={`menu-item-${it.id}`}>
                      <img src={it.image_url} alt={it.name} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{it.name}</div>
                        <div className="text-xs text-[var(--so-text-soft)] line-clamp-2">{it.description}</div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="font-bold text-[var(--so-orange)]">{formatPrice(it.price)}</span>
                          <button
                            onClick={() => addItem(r, it)}
                            data-testid={`add-${it.id}`}
                            className="w-9 h-9 rounded-full bg-[var(--so-orange)] text-white flex items-center justify-center hover:bg-[var(--so-orange-hot)] transition-colors"
                            aria-label="Add to cart"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>

        {count > 0 && (
          <button
            onClick={() => navigate("/app/cart")}
            data-testid="floating-cart-btn"
            className="fixed bottom-24 inset-x-4 md:inset-x-auto md:right-8 z-30 so-btn-primary justify-center md:w-80 shadow-2xl"
          >
            View cart ({count} items)
          </button>
        )}
      </div>
    </CustomerLayout>
  );
}
